import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useCallback } from 'react'
import styled from 'styled-components'

const NavigatorRoot = styled.div`
  position: absolute;
  bottom: 0.375rem;
  left: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  background: ${({ theme }) => theme.forecast.pageBackground1}cc;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: 0.25rem;
  padding: 0.125rem;
  z-index: 2;
  user-select: none;
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border: none;
  border-radius: 0.125rem;
  background: none;
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`

const PageLabel = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 0 0.25rem;
  white-space: nowrap;
`

interface PreviewPageNavigatorProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const PreviewPageNavigator: React.FC<PreviewPageNavigatorProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (currentPage > 1) onPageChange(currentPage - 1)
    },
    [currentPage, onPageChange]
  )

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (currentPage < totalPages) onPageChange(currentPage + 1)
    },
    [currentPage, totalPages, onPageChange]
  )

  if (totalPages <= 1) return null

  return (
    <NavigatorRoot>
      <NavButton
        onClick={handlePrev}
        disabled={currentPage <= 1}
        aria-label='Previous page'>
        <ChevronLeft size={14} />
      </NavButton>
      <PageLabel>
        {currentPage} / {totalPages}
      </PageLabel>
      <NavButton
        onClick={handleNext}
        disabled={currentPage >= totalPages}
        aria-label='Next page'>
        <ChevronRight size={14} />
      </NavButton>
    </NavigatorRoot>
  )
}
